import React, { useState, useEffect } from 'react';
import { schedulerService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeBadge,
  NativeAlert,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  AlertCircle,
  RefreshCw,
  Calendar,
  Play,
  Pause,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// Job isim ve açıklamalarını Türkçeleştir
const translateJobName = (name) => {
  const translations = {
    'Session Timeout Check': 'Oturum Zaman Aşımı Kontrolü',
    'License Expiry Check': 'Lisans Sona Erme Kontrolü',
    'Audit Log Cleanup': 'Denetim Kaydı Temizleme',
    'OCPP Heartbeat Check': 'OCPP Bağlantı Kontrolü',
    'Orphan Photo Cleanup': 'Sahipsiz Fotoğraf Temizleme',
    'Orphan Fotoğraf Temizliği': 'Sahipsiz Fotoğraf Temizleme',
    'Stuck Session Cleanup': 'Takılı Oturum Temizleme',
    'Takılı Oturum Temizleme': 'Takılı Oturum Temizleme',
    'Daily Backup': 'Günlük Yedekleme',
    'Fault Escalation Check': 'Arıza Eskalasyon Kontrolü',
    'Technician Morning Reminder': 'Teknisyen Sabah Hatırlatması',
    'Technician Contract Expiry Check': 'Teknisyen Sözleşme Bitiş Kontrolü',
    'Technician Membership Expiry Check': 'Teknisyen Üyelik Bitiş Kontrolü',
    'TECHNICIAN_MORNING_REMINDER': 'Teknisyen Sabah Hatırlatması',
    'TECHNICIAN_CONTRACT_EXPIRY_CHECK': 'Teknisyen Sözleşme Bitiş Kontrolü',
    'TECHNICIAN_MEMBERSHIP_EXPIRY_CHECK': 'Teknisyen Üyelik Bitiş Kontrolü'
  };
  return translations[name] || name;
};

const translateJobDescription = (desc) => {
  if (!desc) return '';
  const translations = {
    'Check and cleanup sessions that have been active too long': '24 saatten uzun aktif kalan oturumları temizle',
    'Check and warn about expiring licenses': 'Süresi dolmak üzere olan lisansları kontrol et ve uyar',
    'Clean up old audit logs': 'Eski denetim kayıtlarını temizle',
    'Check OCPP charger connectivity': 'OCPP şarj cihazı bağlantı durumunu kontrol et',
    'Clean up orphan photos not linked to any fault': 'Herhangi bir arızaya bağlı olmayan fotoğrafları temizle',
    'Clean up stuck charging sessions': 'Takılı kalmış şarj oturumlarını temizle',
    'Automatic daily backup': 'Otomatik günlük yedekleme',
    'Check and escalate faults based on priority': 'Önceliğe göre arızaları eskalasyon için kontrol et'
  };
  return translations[desc] || desc;
};

export default function SchedulerPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featureAvailable, setFeatureAvailable] = useState(true);
  const [runningJob, setRunningJob] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await schedulerService.jobs();
      if (Array.isArray(data)) {
        setJobs(data);
        setFeatureAvailable(true);
      } else {
        setJobs([]);
        setFeatureAvailable(data !== null);
      }
    } catch (err) {
      if (err.status === 404) {
        setFeatureAvailable(false);
        setJobs([]);
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRunJob = async (job) => {
    const jobId = job.id || job.job_id;
    setRunningJob(jobId);
    try {
      const result = await schedulerService.runJob(jobId);
      if (result) {
        toast.success(`"${job.name}" gorevi calistirildi`);
      } else {
        toast.info('Gorev calistirma ozelligi mevcut degil');
      }
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRunningJob(null);
    }
  };

  const handleToggleJob = async (job) => {
    const jobId = job.id || job.job_id;
    try {
      const result = await schedulerService.toggleJob(jobId, !job.is_active);
      if (result) {
        toast.success(`Gorev ${!job.is_active ? 'etkinlestirildi' : 'devre disi birakildi'}`);
      } else {
        toast.info('Gorev toggle ozelligi mevcut degil');
      }
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!featureAvailable) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="scheduler-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Zamanlanmis Gorevler</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Sistem gorev zamanlayicisi</p>
        </div>
        <NativeAlert variant="warning" icon={AlertTriangle}>
          Zamanlayici ozelligi su an aktif degil. Bu ozellik gelecek guncellemelerde eklenecektir.
        </NativeAlert>
      </div>
    );
  }

  if (error) {
    return (
      <NativeAlert variant="danger" icon={AlertCircle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <NativeButton variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Tekrar Dene
          </NativeButton>
        </div>
      </NativeAlert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="scheduler-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Zamanlanmis Gorevler</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Otomatik sistem görevlerini yönetin</p>
        </div>
        <NativeButton variant="outline" onClick={fetchData}>
          <RefreshCw style={{ width: '16px', height: '16px' }} />
          Yenile
        </NativeButton>
      </div>

      {/* Jobs Table */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Calendar}>Gorev Listesi ({jobs.length})</NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          {jobs.length === 0 ? (
            <EmptyState icon={Calendar} title="Zamanlanmış görev bulunamadı" description="Henüz görev tanımlanmamış" />
          ) : (
            <NativeTable>
              <NativeTableHeader>
                <NativeTableHead>Gorev Adi</NativeTableHead>
                <NativeTableHead>Cron</NativeTableHead>
                <NativeTableHead>Son Calisma</NativeTableHead>
                <NativeTableHead>Durum</NativeTableHead>
                <NativeTableHead align="right">Islemler</NativeTableHead>
              </NativeTableHeader>
              <NativeTableBody>
                {jobs.map((job) => (
                  <NativeTableRow key={job.id || job.job_id} data-testid={`job-row-${job.id || job.job_id}`}>
                    <NativeTableCell>
                      <div>
                        <span style={{ fontWeight: '500' }} className="text-slate-900 dark:text-white">{translateJobName(job.name)}</span>
                        {job.description && (
                          <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '13px', margin: '2px 0 0' }}>{translateJobDescription(job.description)}</p>
                        )}
                      </div>
                    </NativeTableCell>
                    <NativeTableCell>
                      <code style={{ padding: '2px 6px', backgroundColor: '#f1f5f9', borderRadius: '4px', fontSize: '12px' }} className="dark:bg-slate-800">{job.cron_expression || job.schedule || '-'}</code>
                    </NativeTableCell>
                    <NativeTableCell style={{ whiteSpace: 'nowrap' }}>
                      {job.last_run ? new Date(job.last_run).toLocaleString('tr-TR') : '-'}
                    </NativeTableCell>
                    <NativeTableCell>
                      <NativeBadge variant={job.is_active ? 'success' : 'secondary'}>
                        {job.is_active ? 'Aktif' : 'Pasif'}
                      </NativeBadge>
                    </NativeTableCell>
                    <NativeTableCell align="right">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <NativeButton
                          variant="outline"
                          size="sm"
                          onClick={() => handleRunJob(job)}
                          disabled={runningJob === (job.id || job.job_id)}
                          data-testid={`run-job-${job.id || job.job_id}`}
                        >
                          {runningJob === (job.id || job.job_id) ? (
                            <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                          ) : (
                            <Play style={{ width: '16px', height: '16px' }} />
                          )}
                          Calistir
                        </NativeButton>
                        <NativeButton
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleJob(job)}
                          title={job.is_active ? 'Devre Disi Birak' : 'Etkinlestir'}
                        >
                          {job.is_active ? (
                            <Pause style={{ width: '16px', height: '16px' }} />
                          ) : (
                            <Play style={{ width: '16px', height: '16px' }} />
                          )}
                        </NativeButton>
                      </div>
                    </NativeTableCell>
                  </NativeTableRow>
                ))}
              </NativeTableBody>
            </NativeTable>
          )}
        </NativeCardContent>
      </NativeCard>
    </div>
  );
}
